import { LISTED_ON } from '../data/listedOn';

/**
 * "Listed on" strip — a rolling marquee of earned directory/launch badges.
 *
 * Renders NOTHING while LISTED_ON is empty, so it can sit in the page ahead of
 * any listings without showing an empty band.
 *
 * Placed in the closing sign-off band (dark heartwood ground), under the brand
 * lockup + "Spark to business" tagline. Because it's on dark, badges (each a
 * self-contained image with its own light/dark background) read as small cards;
 * the viewport fades to heartwood at both edges.
 *
 * Rolling: the badge set is rendered TWICE in one track and the track animates
 * to -50%, so when it reaches the duplicate it's pixel-identical to the start —
 * a seamless loop with any number of badges. Pauses on hover.
 *
 * Reduced motion: the animation is off and the duplicate set is hidden, leaving
 * a single centered, wrapping row.
 */
/**
 * @param variant
 *   'marquee' (default) — a rolling, edge-faded strip. Best in a WIDE container
 *     (the /spark landing footer, a full-width band). In a narrow column the
 *     roll + edge fade half-hide the first badge and read as broken.
 *   'static' — a plain wrapping row, no roll, no fade. Best in a NARROW column
 *     (the homepage hero, beneath the CTA) where it's the standard "as featured
 *     on" row.
 */
export function ListedOnStrip({
  variant = 'marquee',
  label = 'Listed on',
}: { variant?: 'marquee' | 'static'; label?: string } = {}) {
  if (LISTED_ON.length === 0) return null;

  const renderGroup = (dup: boolean) => (
    <div className="listedon__group" aria-hidden={dup} data-dup={dup ? 'true' : undefined}>
      {LISTED_ON.map((b) => (
        <a
          key={(dup ? 'dup-' : '') + b.name}
          href={b.href}
          target="_blank"
          rel={b.rel ?? 'noopener noreferrer'}
          className={`listedon__badge${b.img ? '' : ' listedon__badge--chip'}`}
          aria-label={`EntreDash on ${b.name}`}
          tabIndex={dup ? -1 : undefined}
        >
          {b.img ? <img src={b.img} alt={`Featured on ${b.name}`} loading="lazy" /> : b.name}
        </a>
      ))}
    </div>
  );

  return (
    <div className={`listedon listedon--${variant}`} aria-label="Listed on">
      <style>{STYLES}</style>
      <div className="listedon__eyebrow">{label}</div>
      <div className="listedon__viewport">
        <div className="listedon__track">
          {renderGroup(false)}
          {/* The duplicate track only exists to make the marquee loop seamless;
              the static variant hides it (CSS). */}
          {variant === 'marquee' && renderGroup(true)}
        </div>
      </div>
    </div>
  );
}

const STYLES = `
  .listedon {
    /* Transparent — lives inside the dark heartwood sign-off band. */
    width: 100%; margin-top: 22px;
    display: flex; flex-direction: column; align-items: center; gap: 14px;
  }
  .listedon__eyebrow {
    font-family: var(--f-mono); font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase;
    /* Muted parchment (not brass) so it doesn't echo the brass "Spark to
       business" tagline sitting right above it. */
    color: rgba(244, 239, 226, 0.42);
  }
  /* Viewport clips the track and fades both edges into the heartwood ground.
     Wide by default so several badges show at once on desktop; capped to 92vw
     so it never overflows a narrow column, where it simply fills the width. */
  .listedon__viewport {
    width: 100%; max-width: min(920px, 92vw); overflow: hidden;
    -webkit-mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
    mask-image: linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent);
  }
  .listedon__track {
    display: flex; width: max-content;
    animation: listedonRoll 26s linear infinite;
  }
  .listedon__track:hover { animation-play-state: paused; }
  .listedon__group {
    display: flex; align-items: center; gap: 26px; padding-right: 26px; flex: none;
  }
  .listedon__badge {
    display: inline-flex; align-items: center; text-decoration: none; flex: none;
    /* A subtle light plate behind every badge so a dark badge (e.g. Dang.ai's
       charcoal card) reads cleanly on the dark heartwood ground, and the whole
       row looks like a consistent set of cards. Light badges just sit on a
       faint lighter pad, which is fine. */
    background: rgba(244, 239, 226, 0.9);
    border-radius: 7px; padding: 5px 10px;
    opacity: 0.96; transition: opacity 150ms ease, transform 150ms ease;
  }
  .listedon__badge:hover { opacity: 1; transform: translateY(-1px); }
  /* Uniform logo-cloud sizing: same small height, auto width, any aspect ratio. */
  .listedon__badge img { display: block; height: 26px; width: auto; max-width: 210px; }
  /* Text-chip fallback (on-dark) — transparent, not the light image plate. */
  .listedon__badge--chip {
    font-family: var(--f-sans); font-size: 13px; font-weight: 600; color: var(--parchment);
    background: transparent;
    border: 1px solid rgba(244, 239, 226, 0.22); border-radius: 999px; padding: 8px 16px;
  }
  .listedon__badge--chip:hover { border-color: var(--brass-light); }

  @keyframes listedonRoll { to { transform: translateX(-50%); } }

  /* Static variant: no roll, no edge fade — a plain wrapping badge row for
     narrow columns (the homepage hero under the CTA). */
  .listedon--static .listedon__viewport {
    max-width: none; overflow: visible;
    -webkit-mask-image: none; mask-image: none;
  }
  .listedon--static .listedon__track { animation: none; width: auto; }
  .listedon--static .listedon__group {
    flex-wrap: wrap; padding-right: 0; gap: 14px 20px;
  }

  /* Respect reduced motion: no roll, hide the duplicate, single centered row. */
  @media (prefers-reduced-motion: reduce) {
    .listedon__viewport {
      display: flex; justify-content: center;
      -webkit-mask-image: none; mask-image: none;
    }
    .listedon__track { animation: none; width: auto; flex-wrap: wrap; justify-content: center; }
    .listedon__group[data-dup='true'] { display: none; }
    .listedon__group { padding-right: 0; gap: 28px; flex-wrap: wrap; justify-content: center; }
  }
  @media (max-width: 600px) {
    .listedon__badge img { height: 24px; max-width: 180px; }
    .listedon__group { gap: 20px; padding-right: 20px; }
  }
`;
